import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { backendUrl } from '../../config/api';
import PasswordField from '../UI/PasswordField';
import { getSafeInternalPath } from '../../utils/safeNavigation';

axios.defaults.withCredentials = true;

const C = {
    bg:     '#060b1f',
    card:   'rgba(13,19,50,0.72)',
    border: 'rgba(255,255,255,0.09)',
    cyan:   '#00d4ff',
    orange: '#ff6b00',
    red:    '#ff3b30',
    green:  '#00c853',
    amber:  '#ffb300',
};

// Popular country codes — sorted: India first, then alphabetical
const COUNTRY_CODES = [
    { code: '91',  flag: '🇮🇳', name: 'India',          maxLen: 10 },
    { code: '1',   flag: '🇺🇸', name: 'USA / Canada',   maxLen: 10 },
    { code: '44',  flag: '🇬🇧', name: 'UK',             maxLen: 10 },
    { code: '61',  flag: '🇦🇺', name: 'Australia',      maxLen: 9  },
    { code: '880', flag: '🇧🇩', name: 'Bangladesh',     maxLen: 10 },
    { code: '55',  flag: '🇧🇷', name: 'Brazil',         maxLen: 11 },
    { code: '86',  flag: '🇨🇳', name: 'China',          maxLen: 11 },
    { code: '20',  flag: '🇪🇬', name: 'Egypt',          maxLen: 10 },
    { code: '33',  flag: '🇫🇷', name: 'France',         maxLen: 9  },
    { code: '49',  flag: '🇩🇪', name: 'Germany',        maxLen: 11 },
    { code: '62',  flag: '🇮🇩', name: 'Indonesia',      maxLen: 12 },
    { code: '98',  flag: '🇮🇷', name: 'Iran',           maxLen: 10 },
    { code: '964', flag: '🇮🇶', name: 'Iraq',           maxLen: 10 },
    { code: '353', flag: '🇮🇪', name: 'Ireland',        maxLen: 9  },
    { code: '972', flag: '🇮🇱', name: 'Israel',         maxLen: 9  },
    { code: '39',  flag: '🇮🇹', name: 'Italy',          maxLen: 10 },
    { code: '81',  flag: '🇯🇵', name: 'Japan',          maxLen: 10 },
    { code: '60',  flag: '🇲🇾', name: 'Malaysia',       maxLen: 10 },
    { code: '52',  flag: '🇲🇽', name: 'Mexico',         maxLen: 10 },
    { code: '977', flag: '🇳🇵', name: 'Nepal',          maxLen: 10 },
    { code: '31',  flag: '🇳🇱', name: 'Netherlands',    maxLen: 9  },
    { code: '64',  flag: '🇳🇿', name: 'New Zealand',    maxLen: 9  },
    { code: '234', flag: '🇳🇬', name: 'Nigeria',        maxLen: 10 },
    { code: '92',  flag: '🇵🇰', name: 'Pakistan',       maxLen: 10 },
    { code: '63',  flag: '🇵🇭', name: 'Philippines',    maxLen: 10 },
    { code: '48',  flag: '🇵🇱', name: 'Poland',         maxLen: 9  },
    { code: '7',   flag: '🇷🇺', name: 'Russia',         maxLen: 10 },
    { code: '966', flag: '🇸🇦', name: 'Saudi Arabia',   maxLen: 9  },
    { code: '27',  flag: '🇿🇦', name: 'South Africa',   maxLen: 9  },
    { code: '82',  flag: '🇰🇷', name: 'South Korea',    maxLen: 10 },
    { code: '34',  flag: '🇪🇸', name: 'Spain',          maxLen: 9  },
    { code: '94',  flag: '🇱🇰', name: 'Sri Lanka',      maxLen: 9  },
    { code: '46',  flag: '🇸🇪', name: 'Sweden',         maxLen: 9  },
    { code: '41',  flag: '🇨🇭', name: 'Switzerland',    maxLen: 9  },
    { code: '886', flag: '🇹🇼', name: 'Taiwan',         maxLen: 9  },
    { code: '66',  flag: '🇹🇭', name: 'Thailand',       maxLen: 9  },
    { code: '90',  flag: '🇹🇷', name: 'Turkey',         maxLen: 10 },
    { code: '971', flag: '🇦🇪', name: 'UAE',            maxLen: 9  },
    { code: '84',  flag: '🇻🇳', name: 'Vietnam',        maxLen: 10 },
];

// Country picker dropdown component
const CountryPicker = ({ value, onChange }) => {
    const [open, setOpen]     = useState(false);
    const [search, setSearch] = useState('');
    const ref = useRef(null);
    const selected = COUNTRY_CODES.find(c => c.code === value) || COUNTRY_CODES[0];

    useEffect(() => {
        const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const filtered = search
        ? COUNTRY_CODES.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || c.code.includes(search))
        : COUNTRY_CODES;

    return (
        <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
            <button type="button" onClick={() => { setOpen(v => !v); setSearch(''); }} style={{
                display: 'flex', alignItems: 'center', gap: 5, padding: '12px 10px',
                background: 'rgba(0,212,255,0.07)', border: `1px solid ${C.border}`,
                borderRadius: 10, cursor: 'pointer', whiteSpace: 'nowrap',
                color: 'rgba(255,255,255,0.8)', fontSize: 14,
            }}>
                <span>{selected.flag}</span>
                <span style={{ fontSize: 13, fontWeight: 700 }}>+{selected.code}</span>
                <span style={{ fontSize: 10, opacity: 0.5 }}>{open ? '▲' : '▼'}</span>
            </button>
            {open && (
                <div style={{
                    position: 'absolute', top: '110%', left: 0, zIndex: 999,
                    background: '#0d1a40', border: `1px solid ${C.border}`,
                    borderRadius: 12, minWidth: 240, maxHeight: 280, overflow: 'hidden',
                    display: 'flex', flexDirection: 'column',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.7)',
                }}>
                    <div style={{ padding: '8px 10px', borderBottom: `1px solid ${C.border}` }}>
                        <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
                            placeholder="Search country…" style={{
                                width: '100%', boxSizing: 'border-box',
                                background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.border}`,
                                borderRadius: 8, padding: '7px 10px', color: '#fff', fontSize: 13, outline: 'none',
                            }} />
                    </div>
                    <div style={{ overflowY: 'auto', flex: 1 }}>
                        {filtered.map(c => (
                            <button key={c.code} type="button" onClick={() => { onChange(c.code); setOpen(false); }} style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                width: '100%', padding: '9px 14px', background: c.code === value ? 'rgba(0,212,255,0.1)' : 'transparent',
                                border: 'none', cursor: 'pointer', color: c.code === value ? C.cyan : 'rgba(255,255,255,0.75)',
                                fontSize: 13, textAlign: 'left', transition: 'background 0.1s',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
                            onMouseLeave={e => e.currentTarget.style.background = c.code === value ? 'rgba(0,212,255,0.1)' : 'transparent'}
                            >
                                <span style={{ fontSize: 18 }}>{c.flag}</span>
                                <span style={{ flex: 1 }}>{c.name}</span>
                                <span style={{ opacity: 0.5, fontFamily: 'monospace' }}>+{c.code}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const inp = {
    width: '100%', boxSizing: 'border-box',
    background: 'rgba(6,11,31,0.8)', border: `1px solid ${C.border}`,
    color: '#fff', borderRadius: 10, padding: '12px 14px', fontSize: 14,
    outline: 'none', transition: 'border-color 0.2s, box-shadow 0.2s',
};

const TABS = [
    { id: 'password', label: '🔑 Password' },
    { id: 'otp',      label: '📱 Mobile OTP' },
];

export default function UserLoginPage() {
    const { login, refreshUser } = useAuth();
    const navigate  = useNavigate();
    const location = useLocation();

    const [tab,      setTab]      = useState('password');
    const [loading,  setLoading]  = useState(false);
    const [error,    setError]    = useState('');
    const [info,     setInfo]     = useState('');

    // password tab
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');

    // OTP tab
    const [countryCode, setCountryCode] = useState('91'); // default India
    const [mobile,      setMobile]      = useState('');
    const [otp,         setOtp]         = useState('');
    const [otpSent,     setOtpSent]     = useState(false);
    const [referral,    setReferral]    = useState('');
    const [otpTimer,    setOtpTimer]    = useState(0);

    const selectedCountry = COUNTRY_CODES.find(c => c.code === countryCode) || COUNTRY_CODES[0];
    // Full number sent to backend: countryCode + localNumber
    const fullMobile = () => countryCode + mobile.replace(/\D/g, '');

    const redirectAfterLogin = user => {
        const redirectTarget = getSafeInternalPath(
            new URLSearchParams(location.search).get('redirect')
        );
        if (redirectTarget) {
            navigate(redirectTarget);
            return;
        }
        if (user.role === 'admin')       navigate('/admincp/admin');
        else if (user.role === 'api_manager') navigate('/api-docs');
        else navigate('/');
    };

    // ── Password login ──────────────────────────────────────────────────────────
    const handlePasswordLogin = async e => {
        e.preventDefault();
        if (!username.trim() || !password.trim()) { setError('Enter username and password'); return; }
        setLoading(true); setError('');
        try {
            const user = await login(username.trim(), password);
            redirectAfterLogin(user);
        } catch (err) {
            setError(err.response?.data?.message || 'Login failed');
        } finally { setLoading(false); }
    };

    // ── Send OTP ────────────────────────────────────────────────────────────────
    const handleSendOtp = async () => {
        const localNum = mobile.replace(/\D/g, '');
        if (!localNum) { setError('Enter your mobile number'); return; }
        if (localNum.length < 7) { setError('Mobile number too short'); return; }
        setLoading(true); setError(''); setInfo('');
        try {
            await axios.post(`${backendUrl}/api/auth/send-otp`, { mobile: fullMobile() });
            setOtpSent(true);
            setInfo(`OTP sent to WhatsApp: +${countryCode} ${mobile}`);
            setOtpTimer(60);
            const t = setInterval(() => setOtpTimer(s => { if (s <= 1) { clearInterval(t); return 0; } return s - 1; }), 1000);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to send OTP');
        } finally { setLoading(false); }
    };

    // ── Verify OTP ──────────────────────────────────────────────────────────────
    const handleVerifyOtp = async e => {
        e.preventDefault();
        if (!otp.trim()) { setError('Enter the OTP'); return; }
        setLoading(true); setError('');
        try {
            const res = await axios.post(`${backendUrl}/api/auth/verify-otp`, {
                mobile:       fullMobile(),
                otp:          otp.trim(),
                referralCode: referral.trim() || undefined,
            });
            await refreshUser();
            redirectAfterLogin(res.data.user);
        } catch (err) {
            setError(err.response?.data?.message || 'OTP verification failed');
        } finally { setLoading(false); }
    };

    const switchTab = t => {
        setTab(t); setError(''); setInfo('');
        setOtpSent(false); setOtp(''); setMobile(''); setCountryCode('91');
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: `${C.bg} radial-gradient(ellipse 100% 40% at 50% 0%, rgba(0,212,255,0.07) 0%, transparent 60%)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }}>
            <style>{`@keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}`}</style>
            <div style={{ width: '100%', maxWidth: 420, animation: 'fadeUp 0.35s ease' }}>

                {/* Logo */}
                <div style={{ textAlign: 'center', marginBottom: 28 }}>
                    <div style={{ fontSize: 48, marginBottom: 8 }}>🎲</div>
                    <div style={{ fontWeight: 900, fontSize: 28, background: `linear-gradient(90deg,${C.cyan},#fff)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', letterSpacing: '-0.5px' }}>
                        LudoKing
                    </div>
                    <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.38)', marginTop: 5 }}>Sign in to play &amp; earn credits</div>
                </div>

                {/* Card */}
                <div style={{
                    background: C.card, backdropFilter: 'blur(24px)',
                    border: `1px solid ${C.border}`, borderRadius: 22, overflow: 'hidden',
                    boxShadow: `0 0 50px rgba(0,212,255,0.07)`,
                }}>
                    {/* Tabs */}
                    <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}` }}>
                        {TABS.map(t => (
                            <button key={t.id} onClick={() => switchTab(t.id)} style={{
                                flex: 1, padding: '14px', fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer',
                                background: tab === t.id ? 'rgba(0,212,255,0.08)' : 'transparent',
                                color: tab === t.id ? C.cyan : 'rgba(255,255,255,0.4)',
                                borderBottom: tab === t.id ? `2px solid ${C.cyan}` : '2px solid transparent',
                                transition: 'all 0.2s',
                            }}>{t.label}</button>
                        ))}
                    </div>

                    <div style={{ padding: '24px 26px' }}>
                        {/* Alerts */}
                        {error && (
                            <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 10, background: 'rgba(255,59,48,0.1)', border: '1px solid rgba(255,59,48,0.3)', color: C.red, fontSize: 13, display: 'flex', gap: 7, alignItems: 'center' }}>
                                <span>⚠</span> {error}
                            </div>
                        )}
                        {info && (
                            <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 10, background: 'rgba(0,200,83,0.1)', border: '1px solid rgba(0,200,83,0.3)', color: C.green, fontSize: 13, display: 'flex', gap: 7, alignItems: 'center' }}>
                                <span>✓</span> {info}
                            </div>
                        )}

                        {/* ── PASSWORD TAB ── */}
                        {tab === 'password' && (
                            <form onSubmit={handlePasswordLogin} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 7 }}>Username</label>
                                    <input value={username} onChange={e => setUsername(e.target.value)} placeholder="your username" autoComplete="username" style={inp}
                                        onFocus={e => { e.target.style.borderColor = C.cyan; e.target.style.boxShadow = '0 0 0 3px rgba(0,212,255,0.1)'; }}
                                        onBlur={e => { e.target.style.borderColor = C.border; e.target.style.boxShadow = 'none'; }} />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 7 }}>Password</label>
                                    <PasswordField value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password" style={inp}
                                        onFocus={e => { e.target.style.borderColor = C.cyan; e.target.style.boxShadow = '0 0 0 3px rgba(0,212,255,0.1)'; }}
                                        onBlur={e => { e.target.style.borderColor = C.border; e.target.style.boxShadow = 'none'; }} />
                                </div>
                                <button type="submit" disabled={loading} style={{
                                    padding: '13px', borderRadius: 12, border: 'none', cursor: loading ? 'wait' : 'pointer',
                                    background: `linear-gradient(135deg,${C.cyan},#0090e7)`,
                                    color: C.bg, fontWeight: 800, fontSize: 15,
                                    boxShadow: `0 0 20px rgba(0,212,255,0.3)`,
                                    opacity: loading ? 0.7 : 1, transition: 'all 0.2s', marginTop: 4,
                                }}>
                                    {loading ? '⏳ Signing in…' : '⚡ Sign In'}
                                </button>
                            </form>
                        )}

                        {/* ── OTP TAB ── */}
                        {tab === 'otp' && (
                            <form onSubmit={otpSent ? handleVerifyOtp : e => { e.preventDefault(); handleSendOtp(); }} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 7 }}>
                                        Mobile Number
                                    </label>
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                                        <CountryPicker value={countryCode} onChange={setCountryCode} />
                                        <input
                                            value={mobile}
                                            onChange={e => setMobile(e.target.value.replace(/\D/g, ''))}
                                            placeholder={`${selectedCountry.maxLen}-digit number`}
                                            maxLength={selectedCountry.maxLen}
                                            type="tel"
                                            inputMode="numeric"
                                            style={{ ...inp, flex: 1 }}
                                            disabled={otpSent}
                                            onFocus={e => { e.target.style.borderColor = C.cyan; e.target.style.boxShadow = '0 0 0 3px rgba(0,212,255,0.1)'; }}
                                            onBlur={e => { e.target.style.borderColor = C.border; e.target.style.boxShadow = 'none'; }}
                                        />
                                    </div>
                                    <div style={{ marginTop: 5, fontSize: 11, color: 'rgba(255,255,255,0.28)' }}>
                                        {selectedCountry.flag} {selectedCountry.name} &nbsp;·&nbsp; Full number: +{countryCode} {mobile || '…'}
                                    </div>
                                </div>

                                {!otpSent && (
                                    <div>
                                        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 7 }}>
                                            Referral Code <span style={{ color: 'rgba(255,255,255,0.2)', fontWeight: 400, textTransform: 'none' }}>(optional — get +10 credits)</span>
                                        </label>
                                        <input value={referral} onChange={e => setReferral(e.target.value.toUpperCase())} placeholder="e.g. AB3X" style={inp}
                                            onFocus={e => { e.target.style.borderColor = C.amber; e.target.style.boxShadow = '0 0 0 3px rgba(255,179,0,0.1)'; }}
                                            onBlur={e => { e.target.style.borderColor = C.border; e.target.style.boxShadow = 'none'; }} />
                                    </div>
                                )}

                                {otpSent && (
                                    <div>
                                        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 7 }}>WhatsApp OTP</label>
                                        <input value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ''))} placeholder="6-digit OTP" maxLength={6} type="tel" inputMode="numeric" style={inp} autoFocus
                                            onFocus={e => { e.target.style.borderColor = C.green; e.target.style.boxShadow = '0 0 0 3px rgba(0,200,83,0.1)'; }}
                                            onBlur={e => { e.target.style.borderColor = C.border; e.target.style.boxShadow = 'none'; }} />
                                        <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                                            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>
                                                Sent via WhatsApp to +{countryCode} {mobile}
                                            </span>
                                            {otpTimer > 0
                                                ? <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>Resend in {otpTimer}s</span>
                                                : <button type="button" onClick={() => { setOtpSent(false); setOtp(''); }} style={{ fontSize: 11, color: C.cyan, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Resend OTP</button>
                                            }
                                        </div>
                                    </div>
                                )}

                                <button type="submit" disabled={loading} style={{
                                    padding: '13px', borderRadius: 12, border: 'none', cursor: loading ? 'wait' : 'pointer',
                                    background: otpSent ? `linear-gradient(135deg,${C.green},#00a843)` : `linear-gradient(135deg,${C.cyan},#0090e7)`,
                                    color: '#fff', fontWeight: 800, fontSize: 15,
                                    boxShadow: otpSent ? `0 0 20px rgba(0,200,83,0.3)` : `0 0 20px rgba(0,212,255,0.3)`,
                                    opacity: loading ? 0.7 : 1, transition: 'all 0.2s',
                                }}>
                                    {loading ? '⏳ Please wait…' : otpSent ? '✓ Verify OTP & Continue' : '📱 Send OTP via WhatsApp'}
                                </button>

                                {!otpSent && (
                                    <div style={{ padding: '12px 14px', borderRadius: 11, background: 'rgba(0,212,255,0.05)', border: `1px solid rgba(0,212,255,0.12)`, fontSize: 12, color: 'rgba(255,255,255,0.45)', lineHeight: 1.6 }}>
                                        📲 We'll send a 6-digit OTP to your WhatsApp.<br />
                                        New accounts get <strong style={{ color: C.green }}>100 credits</strong> free. Use a referral code for <strong style={{ color: C.amber }}>+10 bonus</strong>!
                                    </div>
                                )}
                            </form>
                        )}
                    </div>
                </div>

                {/* Credits info */}
                <div style={{ marginTop: 16, padding: '14px 18px', borderRadius: 14, background: 'rgba(255,179,0,0.06)', border: '1px solid rgba(255,179,0,0.15)', display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'center' }}>
                    {[
                        { icon: '🎁', label: 'New signup', value: '100 cr free' },
                        { icon: '👥', label: 'Refer a friend', value: '+50 cr' },
                        { icon: '✨', label: 'Join via referral', value: '+10 cr bonus' },
                    ].map(({ icon, label, value }) => (
                        <div key={label} style={{ textAlign: 'center', minWidth: 80 }}>
                            <div style={{ fontSize: 20 }}>{icon}</div>
                            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 3 }}>{label}</div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: C.amber }}>{value}</div>
                        </div>
                    ))}
                </div>

                <div style={{ textAlign: 'center', marginTop: 16 }}>
                    <Link to="/" style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', textDecoration: 'none', transition: 'color 0.2s' }}
                        onMouseEnter={e => e.target.style.color = 'rgba(255,255,255,0.65)'}
                        onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.3)'}
                    >← Continue as guest (limited features)</Link>
                </div>
            </div>
        </div>
    );
}
